import { Chip, CircularProgress, InputAdornment, Stack, TextField } from '@mui/material';
import { BaseTheme, IconButton } from 'design-system';
import { useEffect, useRef, useState } from 'react';
import { MdCalendarToday, MdCancel, MdClose, MdFilterList, MdSearch } from 'react-icons/md';
import { useAnalytics } from 'hooks/useAnalytics';
import useIsDarkMode from 'hooks/useIsDarkMode';
import useIsViewport from 'hooks/useIsViewport';
import { SearchAction, SearchActionProps, SearchBarVariant, SearchSuggestion } from './SearchBar.types';
import { DEFAULT_SEARCHBAR_WIDTH } from './SearchBar.utils';

const DEFAULT_SEARCHBAR_HEIGHT = '34px';
const DEFAULT_SEARCHBAR_HEIGHT_MOBILE = '40px';
const LARGE_SEARCHBAR_WIDTH = '400px';

export interface SearchBarFieldProps {
  value?: string;
  loading?: boolean;
  placeholder?: string;
  variant?: SearchBarVariant;
  showClear?: boolean;
  inputProps?: object;
  action?: SearchActionProps;
  sxProps?: object;
  selectedSuggestion?: SearchSuggestion | null;
  onSelectedSuggestionClear?: () => void;
  onFocus?: () => void;
  onBlur?: () => void;
  onChange?: (text: string) => void;
  onClose?: () => void;
}

const SearchActionIcons = {
  [SearchAction.Filter]: <MdFilterList size={20} />,
  [SearchAction.DateRange]: <MdCalendarToday size={20} />,
};

const getSearchBarStyle = (theme: BaseTheme, variant: SearchBarVariant, sxProps = {}) => {
  switch (variant) {
    case SearchBarVariant.GREYED:
      return {
        backgroundColor: theme.palette.common.white,
        ...sxProps,
      };
    case SearchBarVariant.ROUNDED:
      return {
        '& .MuiInputBase-root': {
          backgroundColor: theme.palette.common.white,
          height: DEFAULT_SEARCHBAR_HEIGHT,
          minWidth: DEFAULT_SEARCHBAR_WIDTH,
          borderRadius: theme.spacing(3),
          [theme.breakpoints.down('md')]: {
            height: DEFAULT_SEARCHBAR_HEIGHT_MOBILE,
          },
        },
        ...sxProps,
      };
    case SearchBarVariant.LARGE_ROUNDED:
      return {
        '& .MuiInputBase-root': {
          backgroundColor: theme.palette.common.white,
          minWidth: LARGE_SEARCHBAR_WIDTH,
          borderRadius: theme.spacing(1),
          [theme.breakpoints.down('md')]: {
            height: DEFAULT_SEARCHBAR_HEIGHT_MOBILE,
            minWidth: DEFAULT_SEARCHBAR_WIDTH,
            maxWidth: DEFAULT_SEARCHBAR_WIDTH,
          },
        },
        ...sxProps,
      };
    case SearchBarVariant.FULL_WIDTH_HEIGHT:
      return {
        '& .MuiInputBase-root': {
          backgroundColor: theme.palette.common.white,
          height: '100%',
          minWidth: '100%',
          maxWidth: '100%',
          [theme.breakpoints.down('md')]: {
            height: DEFAULT_SEARCHBAR_HEIGHT_MOBILE,
          },
        },
        ...sxProps,
      };
    default:
      return {
        '& .MuiInputBase-root': {
          backgroundColor: theme.palette.common.white,
          height: DEFAULT_SEARCHBAR_HEIGHT,
          minWidth: DEFAULT_SEARCHBAR_WIDTH,
          maxWidth: DEFAULT_SEARCHBAR_WIDTH,
          [theme.breakpoints.down('md')]: {
            height: DEFAULT_SEARCHBAR_HEIGHT_MOBILE,
          },
        },
        ...sxProps,
      };
  }
};

const SearchBarField = ({
  value,
  loading = false,
  placeholder = 'Search...',
  variant = SearchBarVariant.GENERAL,
  showClear,
  action,
  selectedSuggestion,
  onSelectedSuggestionClear,
  onFocus,
  onBlur,
  onChange,
  sxProps,
}: SearchBarFieldProps) => {
  const isMobile = useIsViewport('md');
  const [actionClicked, setActionClicked] = useState(false);
  const isDarkMode = useIsDarkMode();
  const { logAnalytics } = useAnalytics();

  const [internalValue, setInternalValue] = useState(value ?? '');
  const submittedValuesRef = useRef<Set<string>>(new Set([value ?? '']));

  useEffect(() => {
    const ext = value ?? '';
    if (submittedValuesRef.current.has(ext)) {
      submittedValuesRef.current = new Set([ext]);
      return;
    }
    setInternalValue(ext);
    submittedValuesRef.current = new Set([ext]);
  }, [value]);

  const inputProps = action
    ? {
        endAdornment: (
          <IconButton
            role="actionButton"
            size="small"
            aria-label="close"
            sx={(theme) => ({
              backgroundColor: theme.palette.grey[200],
              borderRadius: '50%',
              padding: theme.spacing(0.75),
              '&:hover': {
                backgroundColor: theme.palette.grey[300],
              },
            })}
            onClick={() => {
              action.onClick();
              setActionClicked(!actionClicked);
            }}
          >
            {action.variant ? SearchActionIcons[action.variant] : <></>}
          </IconButton>
        ),
      }
    : {};

  if (value === undefined || onChange === undefined) {
    return null;
  }
  return (
    <Stack role="searchbarfield" direction="row">
      <TextField
        variant="outlined"
        fullWidth
        placeholder={action?.chipLabel || selectedSuggestion ? '' : placeholder}
        value={internalValue}
        color="primary"
        sx={(theme) => getSearchBarStyle(theme, variant, sxProps)}
        onFocus={onFocus}
        onBlur={onBlur}
        onChange={(e) => {
          const next = e.target.value;
          setInternalValue(next);
          submittedValuesRef.current.add(next);
          onChange(next);
          if (next.trim().length > 0) {
            logAnalytics('search', { value: next });
          }
        }}
        slotProps={{
          input: {
            autoComplete: 'off',
            startAdornment: (
              <InputAdornment position="start">
                {loading ? (
                  <CircularProgress
                    role="loader"
                    size={24}
                    color="secondary"
                    sx={(theme) => ({
                      maxHeight: theme.spacing(2.5),
                      maxWidth: theme.spacing(2.5),
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    })}
                  />
                ) : (
                  <>
                    <MdSearch size={20} />
                    {action?.chipLabel && (
                      <Chip
                        size="small"
                        sx={(theme) => ({
                          fontWeight: theme.typography.fontWeightRegular,
                          marginLeft: theme.spacing(0.5),
                        })}
                        label={action?.chipLabel}
                        variant="filled"
                        color="primary"
                        onClick={() => {
                          action?.onClick();
                          setActionClicked(!actionClicked);
                        }}
                        deleteIcon={<MdCancel size={16} />}
                        onDelete={action?.onClear}
                      />
                    )}
                    {selectedSuggestion && (
                      <Chip
                        role="selectedSuggestionChip"
                        size="small"
                        sx={(theme) => ({
                          fontWeight: theme.typography.fontWeightRegular,
                          marginLeft: theme.spacing(0.5),
                        })}
                        label={selectedSuggestion.label}
                        variant="filled"
                        color="primary"
                        deleteIcon={<MdCancel size={16} />}
                        onDelete={onSelectedSuggestionClear}
                      />
                    )}
                  </>
                )}
              </InputAdornment>
            ),
            ...inputProps,
          },
          htmlInput: {
            role: 'searchbartextfield',
          },
        }}
      />
      {showClear && isMobile && (
        <IconButton
          role="closeButton"
          onClick={() => onChange('')}
          sx={(theme) => ({ color: isDarkMode ? theme.palette.common.white : theme.palette.primary.main })}
        >
          <MdClose size={20} />
        </IconButton>
      )}
    </Stack>
  );
};

export default SearchBarField;
